// utils/fileDownloader.ts
// Minimal types for File System Access API (used when available in Chromium-based browsers)
interface FileSystemWritableFileStream {
  write: (data: Uint8Array) => Promise<void>
  close: () => Promise<void>
}

interface FileSystemFileHandle {
  createWritable: () => Promise<FileSystemWritableFileStream>
}

interface FileSystemAccessWindow {
  showSaveFilePicker?: (options: { suggestedName?: string }) => Promise<FileSystemFileHandle>
}
interface DownloadOptions {
  exportId: string
  accessToken: string
  uri?: string
  filename?: string
  onProgress?: (progress: number) => void
  onError?: (error: Error) => void
  onComplete?: () => void
}

export const DATASET_EXPORT_URL = `${process.env.REACT_APP_API_ENDPOINT}/v1/exports`

export const downloadExport = async ({
  exportId,
  accessToken,
  uri,
  filename,
  onProgress,
  onError,
  onComplete,
}: DownloadOptions): Promise<void> => {
  try {
    // First, get the presigned URL from the API
    const params = new URLSearchParams({
      download: 'false',
      expire: '1440', // 24 hours expiration
    })

    if (uri) {
      params.append('uri', uri)
    }

    // Get the presigned URL
    const urlResponse = await fetch(
      `${DATASET_EXPORT_URL}/${exportId}/content?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    )

    if (!urlResponse.ok) {
      throw new Error(`Failed to get download URL: ${urlResponse.status}`)
    }

    // Parse the response to get the AWS S3 presigned URL
    const { URL: presignedUrl } = await urlResponse.json()

    if (!presignedUrl) {
      throw new Error('No presigned URL was returned from the server')
    }

    // Helper that lets the browser handle the download natively (no buffering in JS)
    const triggerBrowserDownload = async (href: string, name?: string) => {
      const a = document.createElement('a')
      a.href = href
      if (name) a.download = name
      // Some browsers require the element to be in the DOM
      document.body.appendChild(a)
      a.click()
      a.remove()
    }

    // Try a HEAD call first to fetch headers (may be blocked by CORS; ignore on failure)
    let headContentLength: number | undefined
    let headContentDisposition: string | null = null
    let headContentType: string | null = null
    try {
      const headResp = await fetch(presignedUrl, { method: 'HEAD' })
      if (headResp.ok) {
        headContentLength = parseInt(headResp.headers.get('Content-Length') || '0', 10) || undefined
        headContentDisposition = headResp.headers.get('Content-Disposition')
        headContentType = headResp.headers.get('Content-Type')
      }
    } catch {
      // Ignore HEAD failures and proceed
    }

    // Derive suggested filename (from headers if available, else from uri or fallback)
    let suggestedFilename = filename
    if (headContentDisposition) {
      const filenameMatch = headContentDisposition.match(/filename="?([^"]*)"?/)
      if (filenameMatch && filenameMatch[1]) {
        suggestedFilename = filenameMatch[1]
      }
    } else if (uri) {
      suggestedFilename = uri.split('/').pop() || `export-${exportId}.zip`
    } else {
      suggestedFilename = suggestedFilename || `export-${exportId}.zip`
    }

    // If FS Access API is available, stream to disk to avoid memory spikes
    const fsWindow = window as unknown as FileSystemAccessWindow
    const canUseFsAccess = typeof fsWindow.showSaveFilePicker === 'function'
    if (!canUseFsAccess) {
      // Fallback: hand off to the browser download manager
      await triggerBrowserDownload(presignedUrl, suggestedFilename)
      if (onComplete) onComplete()
      return
    }

    // Use GET to start a readable stream
    let s3Response: Response
    try {
      s3Response = await fetch(presignedUrl, { method: 'GET' })
    } catch (err) {
      console.error('Error downloading from S3:', err)
      throw new Error('Download from S3 failed')
    }

    if (!s3Response.ok) {
      throw new Error(`Download from S3 failed with status: ${s3Response.status}`)
    }

    // Check content type to ensure it's a file, not JSON error
    const contentType = s3Response.headers.get('Content-Type') || headContentType
    if (contentType && contentType.includes('application/json')) {
      try {
        const text = await s3Response.text()
        // Try to parse JSON for clearer message
        try {
          const obj = JSON.parse(text)
          throw new Error(`Expected file but received JSON: ${JSON.stringify(obj)}`)
        } catch {
          throw new Error(`Expected file but received JSON/text: ${text}`)
        }
      } catch (e) {
        throw e instanceof Error ? e : new Error('Received JSON when a file was expected')
      }
    }

    // Size for progress (from GET if available, otherwise from HEAD)
    const contentLengthHeader = s3Response.headers.get('Content-Length')
    const total = contentLengthHeader ? parseInt(contentLengthHeader, 10) : headContentLength || 0

    // Stream to disk with FS Access API
    if (!fsWindow.showSaveFilePicker) {
      await triggerBrowserDownload(presignedUrl, suggestedFilename)
      if (onComplete) onComplete()
      return
    }
    const fileHandle = await fsWindow.showSaveFilePicker({
      suggestedName: suggestedFilename,
    })
    const writable = await fileHandle.createWritable()

    const body: ReadableStream<Uint8Array> | null = s3Response.body
    const reader = body?.getReader()
    if (!reader) {
      // If streaming is not available, fall back to browser download
      await writable.close()
      await triggerBrowserDownload(presignedUrl, suggestedFilename)
      if (onComplete) onComplete()
      return
    }

    let received = 0
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) {
        await writable.write(value)
        received += value.length
        if (onProgress && total) {
          const progressPercentage = Math.min((received / total) * 100, 100)
          onProgress(progressPercentage)
        }
      }
    }

    await writable.close()

    if (onComplete) onComplete()
  } catch (err) {
    if (onError && err instanceof Error) {
      onError(err)
    } else if (onError) {
      onError(new Error('An unknown error occurred'))
    }
  }
}
